import React, { useContext, useEffect, useState, SetStateAction } from "react";
import { Form, Col } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import $ from "jquery";
import {
    hash,
    getFileFromHash,
    fetchAssetByID,
    getCountryCode,
    uploadFiles,
    resetAssetModal,
    getClientId,
} from "@ts/utils";
import { showToast } from "@ts/modals";
import ImageSpecificationsCard from "./ImageSpecificationsCard";
import { mainContext } from "@ts/constants";
import PopulateAssetGrid from "./PopulateAssetGrid";
import { FILE_SPECIFICATIONS } from "@ts/constants";
import {
    AssetFormErrors,
    IChannel,
    IMainContext,
    IStrapiAssetResponse,
    IStrapiResponse,
    THashConfig,
} from "@ts/common-types";
import { useSelector } from "react-redux";
import usePermissionCheck from "@/hooks/usePermissionCheck";

interface IAssetProps {
    assetResponse: IStrapiAssetResponse | IStrapiResponse[] | [];
    errors: Record<string, string>;
    setErrors: React.Dispatch<SetStateAction<Record<string, string>>>;
    setCampaignField: (field: string, value: string) => void;
}

type AssetForm = {
    file?: File;
    assetName?: string;
};

export const AssetUploadModal = ({
    assetResponse,
    errors,
    setErrors,
    setCampaignField,
}: IAssetProps) => {
    const [assetData, setAssetData] = useState([]);
    const [form, setForm] = useState({});
    const [value, setValue] = useState("");
    const { campaignCreationConfigParams, setAssetResponse, setActivePage } =
        useContext<IMainContext>(mainContext);
    const channel = useSelector((state: IChannel) => state.channel.value);

    const setField = (field: string, fieldValue: string) => {
        setForm((prevState) => ({
            ...prevState,
            [field]: fieldValue,
        }));

        if (errors[field])
            setErrors({
                ...errors,
                [field]: null,
            });
    };

    function assetClickHandler() {
        const a = document.querySelectorAll(".assetCard");
        a.forEach((element) => {
            (element as HTMLAnchorElement).onclick = function () {
                a.forEach((el) => {
                    el.classList.remove("border", "border-3", "border-primary");
                });
                $("#select-asset").prop("disabled", false);
                $(this).addClass("border border-3 border-primary");
                $("#firstStep").hasClass("showing")
                    ? sessionStorage.setItem(
                          "strapiAssetID",
                          (this as HTMLElement).dataset.id
                      )
                    : sessionStorage.setItem(
                          "campaignCustomImageStrapiAssetID",
                          (this as HTMLElement).dataset.id
                      );
            };
        });
    }

    function doConfirm(msg: string, yesFn: () => void, noFn: () => void) {
        const confirmBox = $("#confirmBox");
        confirmBox.find(".message").text(msg);
        confirmBox
            .find(".yes,.no")
            .unbind()
            .click(function () {
                confirmBox.hide();
            });
        confirmBox.find(".yes").click(yesFn);
        confirmBox.find(".no").click(noFn);
        confirmBox.show();
    }

    async function onFileInput(event: React.SyntheticEvent) {
        $("#confirmBox").hide();
        const target = event.target as HTMLInputElement;

        const fileInput = document.getElementById(
            "upload-file"
        ) as HTMLInputElement;
        const fileExt = fileInput.value.split(".").pop();

        const isImageAsset =
            !!FILE_SPECIFICATIONS.image.supportedExtensions.filter(
                (ext) => ext.toLowerCase() === fileExt.toLowerCase()
            ).length;
        const isVideoAsset =
            !!FILE_SPECIFICATIONS.video.supportedExtensions.filter(
                (ext) => ext.toLowerCase() === fileExt.toLowerCase()
            ).length;

        if (!isImageAsset && !isVideoAsset) {
            showToast("danger", "Unsuppported File Type!");
            fileInput.value = "";
        } else if (
            isImageAsset &&
            target.files[0].size > FILE_SPECIFICATIONS.image.maxFileSize
        ) {
            showToast("danger", "File size exceeded! Should be less than 5MB");
            fileInput.value = "";
        } else if (
            isVideoAsset &&
            target.files[0].size > FILE_SPECIFICATIONS.video.maxFileSize
        ) {
            showToast(
                "danger",
                "File size exceeded! Should be less than 500MB"
            );
            fileInput.value = "";
        } else {
            setField("file", target.files[0].name);

            const file = target.files[0];
            const buffer = await file.arrayBuffer();

            const hashConfig: THashConfig = {
                fileName: target.files[0].name,
                fileSize: buffer.byteLength,
                fileExt: target.files[0].name.split(".").pop(),
                clientId: getClientId(),
            };

            const fileHash = await hash(hashConfig);
            sessionStorage.setItem("fileHash", fileHash);

            const response = await getFileFromHash("fileHash");

            const fileNameInput = document.getElementById(
                "assetname"
            ) as HTMLInputElement;
            if (response.assetResponse.length) {
                $("#confirmBox").show();
                doConfirm(
                    "Selected image is already uploaded! Do you wish to use it?",
                    async function yes() {
                        const strapiAssetID =
                            response.assetResponse[0].strapi_id;
                        const assetInfo = await fetchAssetByID(strapiAssetID);

                        fileNameInput.value = assetInfo.data.attributes.name;
                        setField("assetName", assetInfo.data.attributes.name);
                        fileNameInput.setAttribute("disabled", "");

                        if ($("#firstStep").hasClass("showing")) {
                            sessionStorage.setItem(
                                "strapiAssetID",
                                strapiAssetID
                            );
                        } else {
                            sessionStorage.setItem(
                                "campaignCustomImageStrapiAssetID",
                                strapiAssetID
                            );
                        }
                    },
                    function no() {
                        const uploadForm = document.getElementById(
                            "upload-form"
                        ) as HTMLFormElement;
                        fileNameInput.removeAttribute("disabled");
                        uploadForm.reset();
                        setForm({});
                    }
                );
            } else {
                fileNameInput.value = "";
                fileNameInput.removeAttribute("disabled");
            }

            setField("file", target.files[0].name);
        }
    }

    useEffect(() => {
        if ((assetResponse as IStrapiAssetResponse).data) {
            setAssetData((assetResponse as IStrapiAssetResponse).data);
        }
    }, [assetResponse]);

    const validateForm = () => {
        const { file, assetName }: AssetForm = form;
        const newErrors: AssetFormErrors = {};

        if (!file) newErrors.file = "Please select a file!";
        if (!assetName || assetName === "")
            newErrors.assetName = "Please enter the file name";
        if (!value) newErrors.assetType = "Please select file type";
        return newErrors;
    };

    async function handleFileSubmit(event: React.SyntheticEvent) {
        event.preventDefault();

        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
        } else {
            const fileName = (
                document.getElementById("assetname") as HTMLInputElement
            ).value;
            if (
                sessionStorage.getItem("strapiAssetID") &&
                $("#firstStep").hasClass("showing")
            ) {
                (
                    document.getElementById("file-name") as HTMLInputElement
                ).value = fileName;
                setCampaignField("fileName", fileName);
                (
                    document.getElementById("file-name") as HTMLInputElement
                ).readOnly = true;

                resetAssetModal(setActivePage);
            } else if (
                sessionStorage.getItem("campaignCustomImageStrapiAssetID") &&
                $("#secondStep").hasClass("showing")
            ) {
                const retrievedNode =
                    campaignCreationConfigParams.api.getDisplayedRowAtIndex(
                        campaignCreationConfigParams.node.rowIndex
                    );
                retrievedNode.setDataValue("asset_name", fileName);

                resetAssetModal(setActivePage);
            } else {
                $("#upload-loader").show();
                $("#upload-msg").show();

                // const data : AssetFormData = {};

                const formData = new FormData();
                const country = getCountryCode();

                const selectedFile = (
                    document.getElementById("upload-file") as HTMLInputElement
                ).files[0];
                const file_name = (
                    document.getElementById("assetname") as HTMLInputElement
                ).value;
                const file_type = value;

                formData.append("files", selectedFile, selectedFile.name);
                formData.append("title", file_name);
                formData.append("asset_type", file_type);
                formData.append("country", country);
                formData.append("channel", channel.toString());
                formData.append("fileHash", sessionStorage.getItem("fileHash"));

                const res = await uploadFiles(formData);
                if (res.status) {
                    $("#upload-loader").hide();
                    $("#upload-msg").hide();
                    showToast("success", res.message);

                    if ($("#firstStep").hasClass("showing")) {
                        sessionStorage.setItem(
                            "strapiAssetID",
                            res.strapiAssetID
                        );

                        (
                            document.getElementById(
                                "file-name"
                            ) as HTMLInputElement
                        ).value = file_name;
                        setCampaignField("fileName", file_name);
                        (
                            document.getElementById(
                                "file-name"
                            ) as HTMLInputElement
                        ).readOnly = true;
                    } else {
                        const customAssetMap = sessionStorage.getItem(
                            "customAssetMapping"
                        )
                            ? new Map(
                                  Object.entries(
                                      JSON.parse(
                                          sessionStorage.getItem(
                                              "customAssetMapping"
                                          )
                                      )
                                  )
                              )
                            : new Map();

                        sessionStorage.setItem(
                            "campaignCustomImageStrapiAssetID",
                            res.strapiAssetID
                        );
                        const retrievedNode =
                            campaignCreationConfigParams.api.getDisplayedRowAtIndex(
                                campaignCreationConfigParams.node.rowIndex
                            );
                        retrievedNode.setDataValue("asset_name", file_name);

                        customAssetMap.set(
                            campaignCreationConfigParams.data.name,
                            res.strapiAssetID
                        );
                        const customImageMapObject =
                            Object.fromEntries(customAssetMap);
                        sessionStorage.setItem(
                            "customAssetMapping",
                            JSON.stringify(customImageMapObject)
                        );
                    }
                } else {
                    showToast("danger", res.message);
                }
                setValue("");
                resetAssetModal(setActivePage);
            }
            setForm({});
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setValue(e.target.value);
    };

    const { canListAssets, canCreateAssets } = usePermissionCheck();


    return (
        <div>
            <Tabs
                defaultActiveKey="selectFile"
                id="asset_upload_tab"
                onSelect={(k) => {
                    if (k === "selectFile") {
                        $("#select-asset").show();
                        $("#upload-asset").hide();
                    } else {
                        $("#select-asset").hide();
                        $("#upload-asset").show();
                    }
                }}
            >
                <Tab eventKey="selectFile" title="Select File">
                    {canListAssets ?
                        <PopulateAssetGrid
                            {...{ assetClickHandler, assetData, setAssetResponse }}
                        /> : null
                    }

                </Tab>
                <Tab eventKey="uploadFile" title="Upload File">
                    <Col className="mx-auto py-5">
                        <ImageSpecificationsCard />
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Form
                                id="upload-form"
                                onSubmit={(event) => handleFileSubmit(event)}
                            >
                                <div className="mb-3 p-5 border border-2 border-dashed rounded-3 border-secondary bg-light text-center">
                                    <Form.Group
                                        as={Col}
                                        controlId="upload-file"
                                        style={{ width: "500px" }}
                                        className="mb-3"
                                    >
                                        <Form.Control
                                            type="file"
                                            onChange={onFileInput}
                                            isInvalid={!!errors.file}
                                            disabled={!canCreateAssets}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.file}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className="col mb-3" id="confirmBox">
                                    <div className="message"></div>
                                    <span className="button yes">Yes</span>
                                    <span className="button no">No</span>
                                </div>

                                <Form.Group as={Col} controlId="assetname">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        onChange={(e) => {
                                            setField(
                                                "assetName",
                                                e.target.value
                                            );
                                        }}
                                        isInvalid={!!errors.assetName}
                                        type="text"
                                        disabled={!canCreateAssets}
                                    />

                                    <Form.Control.Feedback type="invalid">
                                        {errors.assetName}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group as={Col} controlId="asset_type">
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select
                                        className="form-select"
                                        onChange={handleChange}
                                        isInvalid={!!errors.assetType}
                                        disabled={!canCreateAssets}
                                    >
                                        <option selected={true} value={null}>
                                            ---Select Asset Type---
                                        </option>
                                        <option value="logo">Logo</option>
                                        <option value="creative_static">
                                            Creative Static
                                        </option>
                                        <option value="creative_video">
                                            Creative Video
                                        </option>
                                    </Form.Select>

                                    <Form.Control.Feedback type="invalid">
                                        Please enter a valid asset type
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Form>
                        </div>
                    </Col>
                </Tab>
            </Tabs>
        </div>
    );
};
